
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

// Import des composants métier
import InfoUserPagesSA from './InfoUserPagesSA';


// ----------------------------- Configuration Dexie -----------------------------
const db = new Dexie('SchoolDatabase');
db.version(1).stores({
  user: 'userId, data, updatedAt'
});

// Sauvegarde des infos utilisateur dans IndexedDB
const saveUserToCache = async (userId, userData) => {
  await db.user.put({
    userId,
    data: JSON.stringify(userData),
    updatedAt: new Date().toISOString()
  });
};

// Récupération depuis le cache
const getUserFromCache = async (userId) => {
  const record = await db.user.get(userId);
  if (record && record.data) {
    return JSON.parse(record.data);
  }
  return null;
};

// Vérifier si le cache utilisateur est récent (moins de 15 minutes)
const isUserCacheRecent = async (userId, maxAgeMinutes = 15) => {
  const record = await db.user.get(userId);
  if (!record || !record.updatedAt) return false;
  const age = (new Date() - new Date(record.updatedAt)) / (1000 * 60);
  return age <= maxAgeMinutes;
};

// ----------------------------- Composant principal -----------------------------
const InfoUserPages = () => {

  const navigate = useNavigate();

  // États
  const [userId] = useState(() => localStorage.getItem('userId'));
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [usingCachedUser, setUsingCachedUser] = useState(false);

  // Détection de connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Déconnexion propre (supprime aussi le cache IndexedDB)
  const handleLogout = useCallback(async () => {
    console.warn('Statut utilisateur désactivé ou session invalide.');
    localStorage.clear();
    if (userId) await db.user.delete(userId);
    navigate('/');
  }, [navigate, userId]);

  // Chargement des infos utilisateur (API + cache)
  useEffect(() => {
    if (!userId) {
      console.warn('userId introuvable.');
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadUserData = async () => {
      try {
        // --- Cas hors ligne : uniquement le cache ---
        if (!isOnline) {
          const cachedData = await getUserFromCache(userId);
          if (cachedData) {
            setUserData(cachedData);
            setUsingCachedUser(true);
            setLoading(false);
          } else {
            setLoading(false);
            console.error('Hors ligne et aucune donnée utilisateur en cache');
          }
          return;
        }

        // --- En ligne : stratégie "cache d'abord, puis réseau" ---
        const cacheRecent = await isUserCacheRecent(userId, 15);
        if (cacheRecent) {
          const cachedData = await getUserFromCache(userId);
          if (cachedData) {
            setUserData(cachedData);
            setUsingCachedUser(true);
            setLoading(false);
            // Rafraîchissement en arrière‑plan
          }
        }

        // Appel API
        const response = await axios.get(`${API_CONFIG.apiUrl}getUserInfo.php`, {
          params: { userId },
          signal: controller.signal,
          timeout: 10000
        });

        const data = response.data;

        if (data) {
          if (data.status === 0) {
            await handleLogout();
            return;
          }
          setUserData(data);
          setUsingCachedUser(false);
          await saveUserToCache(userId, data);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Requête annulée');
        } else if (error.code === 'ECONNABORTED' || error.message.includes('Network Error')) {
          // Erreur réseau → fallback cache
          const cachedData = await getUserFromCache(userId);
          if (cachedData) {
            setUserData(cachedData);
            setUsingCachedUser(true);
            console.log('Utilisation du cache utilisateur (réseau indisponible)');
          } else {
            console.error('Impossible de charger les données utilisateur');
          }
        } else {
          console.error('Erreur API:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadUserData();

    return () => {
      controller.abort();
    };
  }, [userId, isOnline, handleLogout]);

  // Mapping des composants selon le rôle
  const ComponentToRender = useMemo(() => {
    if (!userData) return null;

    const roleComponents = {
      'SA': InfoUserPagesSA,
     
      
    };

    const SpecificComponent = roleComponents[userData.fonction];

    return SpecificComponent ? (
      <SpecificComponent userId={userId} fonction={userData.fonction}  />
    ) : (
      <div className="text-center mt-5 text-white">Rôle non reconnu ou accès non autorisé.</div>
    );
  }, [userData, userId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-dark text-white">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="adminx-container">
  
      {ComponentToRender}
    </div>
  );
};

export default InfoUserPages;